import { USER_EMAIL, USER_UUID } from 'app-constants'
import { sendToSentry } from 'utils/errors'

const PREFETCH_EVENT = 'advocate_mobile_experience'
const CREATE_TOKEN = '/api/v5/token'
const UPDATE_PROFILE = '/api/v6/zones'
const FIND_SHAREABLE = '/api/v4/shareable'

let BASE_URL: string // Extole base url

type RequestType = 'GET' | 'POST'

enum ErrorTypes {
  Request = 'ExtoleRequestError',
  AccessToken = 'NoExtoleAccessTokenError',
}

interface ProfileBody {
  event_name: string
  data: ReferralData | PersonalData
}

interface ReferralData {
  advocate_code: string // Referral code of advocate who referred registering user
}

interface PersonalData {
  partner_user_id: string // Acorns UUID
  first_name: string // User's first name
  last_name: string // User's last name
}

interface TokenResponse {
  access_token: string // User's access token in Extole
  expires_in: number // Number of seconds until token expires
  scopes: string[] // Functions that can be performed to user's profile with token
}

interface ProfileResponse {
  event_id: string // ID of event
  data: object
}

interface ShareableResponse {
  person_id: string
  shareable_id: string
  code: string
  link: string
  key: string
  content: object
}

/**
 * Sends HTTP request. JSON response is returned if the request was successful.
 * @param  method        Type of request (GET or POST).
 * @param  url           URL to send request to.
 * @param  customHeaders Custom Headers for request
 * @param  body          Data to be sent to the server.
 * @return Response from Extole server.
 */
const fetchData = async (
  method: RequestType,
  url: string,
  body?: string | ProfileBody,
  customHeaders?: object,
): Promise<TokenResponse | ProfileResponse | ShareableResponse[]> => {
  const options = {
    method,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...customHeaders,
    },
    ...(body && { body: typeof body === 'string' ? body : JSON.stringify(body) }),
  }

  try {
    return (await fetch(`${BASE_URL}${url}`, options)).json()
  } catch (error) {
    sendToSentry(error, {
      name: ErrorTypes.Request,
      breadcrumb: `Attempt to make ${method} request to Extole at ${url.split('?')[0]} failed`,
    })
  }
}

/**
 * Sets global variables to be used when fetching data.
 * @param env All environment variables.
 */
const setUpRequest = env => {
  BASE_URL = env.get('extoleUrl')
}

/**
 * Creates an access token in Extole (either anonymous or associated with email).
 * @param  env   Environment variables.
 * @param  email User's email.
 * @return Access token for user to Extole.
 */
const getExtoleAccessToken = async (env, email?: string): Promise<string> => {
  setUpRequest(env)
  const body = email ? `email=${encodeURIComponent(email)}` : null
  const response = (await fetchData('POST', `${CREATE_TOKEN}`, body, {
    'Content-Type': 'application/x-www-form-urlencoded',
  })) as TokenResponse
  return response?.access_token
}

/**
 * Creates a profile for a user in Extole by creating an access token. The profile is updated with a referral code
 * if that user was referred.
 * @param env          Environment variables.
 * @param email        User's email.
 * @param referralCode Invitation code of referree.
 */
export const createExtoleProfile = async (
  env,
  email: string,
  referralCode?: string,
): Promise<void> => {
  const accessToken = await getExtoleAccessToken(env, email)

  if (referralCode) {
    await fetchData('POST', `${UPDATE_PROFILE}?access_token=${accessToken}`, {
      event_name: PREFETCH_EVENT,
      data: {
        advocate_code: referralCode,
      },
    })
  }
}

/**
 * Assigns Acorns UUID and personal info to user's profile in Extole and creates a referral link.
 * @param env       Environment variables.
 * @param firstName User's first name.
 * @param lastName  User's last name.
 */
export const updateExtolePerson = async (
  env,
  firstName: string,
  lastName: string,
): Promise<void> => {
  const uuid = window.sessionStorage.getItem(USER_UUID)
  const email = window.sessionStorage.getItem(USER_EMAIL)
  const accessToken = await getExtoleAccessToken(env, email)

  if (accessToken) {
    await fetchData('POST', `${UPDATE_PROFILE}?access_token=${accessToken}`, {
      event_name: PREFETCH_EVENT,
      data: {
        partner_user_id: uuid,
        first_name: firstName,
        last_name: lastName,
      },
    })
  } else {
    sendToSentry(new Error('Null Extole access token'), {
      name: ErrorTypes.AccessToken,
      message: 'Extole profile cannot be updated without an access token.',
    })
  }
}

/**
 * Determines if a referral code is valid or not.
 * @param  env  Environment variables.
 * @param  code Referral code.
 * @return True if referral code is valid; otherwise, throw error.
 */
export const isReferralCodeValid = async (env, code: string): Promise<boolean> => {
  const accessToken = await getExtoleAccessToken(env)
  const shareables = (await fetchData(
    'GET',
    `${FIND_SHAREABLE}?access_token=${accessToken}&code=${code}`,
  )) as ShareableResponse[]

  if (!shareables.length) {
    throw new Error('Referral code is either invalid or cannot be validated in Extole')
  }

  return true
}
